import BaseStepHandler from './BaseStepHandler';
import { Image } from 'fabric';

/**
 * Handler for image step type with optimized export handling
 */
export default class ImageStepHandler extends BaseStepHandler {
    /**
     * Create a new ImageStepHandler
     */
    constructor(stepId, stepDefinition, canvas, stepManager, side) {
        super(stepId, stepDefinition, canvas, stepManager, side);
        this.originalDimensions = null;
        this.modificationHandler = null;
        this.imageId = null; // Track the unique ID for this image
    }
    
    /**
     * Initialize image elements
     */
    initialize() {
        super.initialize();
        
        // Get the main element ID from definition
        const elementId = this.definition.elements?.[0];
        if (!elementId) return this;
        
        // Get element from canvas
        let imageElement = this.canvas.getElement(elementId);
        
        // Store reference if element exists
        if (imageElement) {
            this.elements.set(elementId, imageElement);
            
            // Store original dimensions for later use
            this.originalDimensions = {
                width: imageElement.width * imageElement.scaleX,
                height: imageElement.height * imageElement.scaleY,
                scaleX: imageElement.scaleX,
                scaleY: imageElement.scaleY
            };
        }
        
        // Generate a unique image ID for this step if not already set
        if (!this.imageId) {
            this.imageId = `${this.side}_${this.stepId}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        }
        
        return this;
    }
    
    /**
     * Process template - handles both new templates and edit mode with existing images
     */
    processTemplate() {
        const stepData = this.getStepData();

        // If we have step data (from user modifications), use it
        if (stepData && Object.keys(stepData).length > 0) {
            this.update(stepData);
            return this;
        }

        // Otherwise, check if template has an existing image (edit mode)
        const elementId = this.definition.elements?.[0];
        if (elementId) {
            const elementData = this.canvas.getElementData(elementId);

            // If template has an image URL (not placeholder), load it into step data
            if (elementData?.data?.src && !elementData.data.src.includes('placeholder.svg')) {
                const initialStepData = {
                    imageUrl: elementData.data.src,
                    enabled: true,
                    left: elementData.data.left,
                    top: elementData.data.top,
                    scaleX: elementData.data.scaleX,
                    scaleY: elementData.data.scaleY
                };

                // Preserve original image reference data for edit mode
                // so we don't re-upload images that haven't changed
                if (!elementData.data.src.startsWith('data:')) {
                    initialStepData.isExistingUpload = true;
                    initialStepData.existingUrl = elementData.data.src;
                    initialStepData.attachmentId = elementData.data.imageId || null;
                }

                // Initialize step data and update display
                this.setStepData(initialStepData);
                this.update(initialStepData);
                return this;
            }
        }

        // No data and no template image, show/hide based on required flag
        if (this.definition.required && !this.definition.startHidden) {
            this.show();
        } else {
            this.hide();
        }

        return this;
    }

    /**
     * Activate image element
     */
    async activate() {
        super.activate();

        let elements = this.getStepElements();

        elements.forEach((element) => {
            element.set({
                selectable: true,
                hasControls: this.definition.allowedActions?.includes('scale') ?? true,
                lockMovementX: !(this.definition.allowedActions?.includes('move') ?? false),
                lockMovementY: !(this.definition.allowedActions?.includes('move') ?? false),
                cornerStyle: 'circle',
                transparentCorners: false,
                cornerColor: '#0d6efd',
                cornerSize: 50,
                padding: 10,
                evented: true,
                borderScaleFactor:10,
            });
            
            // Make active on canvas
            this.canvas.canvas.setActiveObject(element);
            this.attachModificationListener(element);
        });

        this.canvas.canvas.requestRenderAll();
        
        return this;
    }
    
    /**
     * Deactivate image element
     */
    deactivate() {
        super.deactivate();
        
        // Get main element
        const elementId = this.definition.elements?.[0];
        if (!elementId) return this;
        
        const element = this.elements.get(elementId);
        if (!element) return this;
        
        // Make non-interactive
        element.set({
            selectable: false,
            hasControls: false,
            lockMovementX: true,
            lockMovementY: true,
            evented: false
        });
        
        // Detach modification listener
        this.detachModificationListener(element);
        
        this.canvas.canvas.requestRenderAll();
        
        return this;
    }

    show() {
        this.ensureElementExists().then((element) => {
            element.set('visible', true);
            this.canvas.canvas.requestRenderAll();
        });
    }

    hide() {
        this.ensureElementExists().then((element) => {
            element.set('visible', false);
            this.canvas.canvas.requestRenderAll();
        });
    }
    
    /**
     * Update image element with step data
     * 
     * @param {object} stepData - Step data
     */
    update(stepData) {
        super.update(stepData);

        // If disabled, hide and return early
        if (stepData?.enabled === false || stepData.needsBackgroundRemoval) {
            this.hide();
            return this;
        }
        
        // Make sure element is visible
        this.show();
        
        // Get the element, creating it if needed
        this.ensureElementExists().then(element => {
            if (!element) return;
            
            // Update image source if provided
            if (stepData.imageUrl && (!element._lastSrc || element._lastSrc !== stepData.imageUrl)) {
                // Store the URL to avoid unnecessary reloading
                element._lastSrc = stepData.imageUrl;
                
                // Set a loading flag for later use
                element._isCustomImageLoading = true;
                
                // Set source and handle loaded event
                element.setSrc(stepData.imageUrl).then(() => {
                    // Check if we have positioning data from edit mode
                    const hasPositioningData = stepData.scaleX !== undefined && stepData.scaleY !== undefined;

                    // If this is a user-uploaded image and we have original dimensions and NO positioning data
                    if (this.originalDimensions && element._isCustomImageLoading && !hasPositioningData) {
                        // Calculate scaling to match original dimensions
                        const scaleX = this.originalDimensions.width / element.width;
                        const scaleY = this.originalDimensions.height / element.height;

                        // Use the smaller scale to maintain aspect ratio and fit within bounds
                        const scale = Math.min(scaleX, scaleY);

                        // Apply scale
                        element.set({
                            scaleX: scale,
                            scaleY: scale
                        });

                        // Update step data with new scale
                        stepData.scaleX = scale;
                        stepData.scaleY = scale;

                        // Update step data without triggering a recursive update
                        this.setStepData(stepData, false);

                        // Clear loading flag
                        element._isCustomImageLoading = false;
                    }

                    // Apply positioning from stepData (for edit mode or saved positions)
                    if (stepData.scaleX !== undefined) element.set('scaleX', stepData.scaleX);
                    if (stepData.scaleY !== undefined) element.set('scaleY', stepData.scaleY);
                    if (stepData.left !== undefined) element.set('left', stepData.left);
                    if (stepData.top !== undefined) element.set('top', stepData.top);

                    // Update stored dimensions with final scale
                    if (this.originalDimensions) {
                        this.originalDimensions.width = element.width * element.scaleX;
                        this.originalDimensions.height = element.height * element.scaleY;
                    }

                    this.canvas.canvas.requestRenderAll();
                });
            } else {
                // No image URL change, but update positioning if needed
                if (stepData.scaleX !== undefined) element.set('scaleX', stepData.scaleX);
                if (stepData.scaleY !== undefined) element.set('scaleY', stepData.scaleY);
                if (stepData.left !== undefined) element.set('left', stepData.left);
                if (stepData.top !== undefined) element.set('top', stepData.top);

                this.canvas.canvas.requestRenderAll();
            }
        });
        
        return this;
    }
    
    /**
     * Handle image upload
     * 
     * @param {File} file - The uploaded file
     */
    async handleImageUpload(file) {
        try {
            // Get existing step data
            const stepData = this.getStepData() || {};
            
            // Create a data URL from the file
            const reader = new FileReader();
            reader.onload = (e) => {
                const imageUrl = e.target.result;
                
                // Update image preview in panel
                const imagePreview = this.panelElement.querySelector('.stc-image-preview');
                if (imagePreview) {
                    imagePreview.innerHTML = `<img src="${imageUrl}" alt="Uploaded image">`;
                }
                
                // Update step data with new image URL and metadata
                stepData.imageUrl = imageUrl;
                stepData.file = file;
                stepData.fileName = file.name;
                stepData.fileType = file.type;
                stepData.fileSize = file.size;
                stepData.uploadTimestamp = Date.now();
                stepData.enabled = true;
                
                // Set background removal setting from definition if not set already
                if (stepData.needsBackgroundRemoval === undefined && this.definition.needsBackgroundRemoval !== undefined) {
                    stepData.needsBackgroundRemoval = this.definition.needsBackgroundRemoval;
                }

                // Save the image ID for reference
                stepData.imageId = this.imageId;
                
                // Ensure the element exists before updating
                this.ensureElementExists().then(() => {
                    // Update element with new image
                    this.setStepData(stepData);
                    this.rebuildPanel();
                    
                    // Check if we should show background removal notification on first upload
                    if (this.definition.backgroundRemovalInfo?.showNotification && 
                        this.stepId === 'image') {
                        this.showBackgroundRemovalHelp();
                    }
                });
            };
            
            reader.readAsDataURL(file);
        } catch (error) {
            console.error('Error handling image upload:', error);
        }
    }

    /**
     * Show background removal notification
     */
    showBackgroundRemovalHelp() {
        console.log('Showing background removal help');
        // Emit event to notify Designer to show the popup
        const event = new CustomEvent('stc:image:needsBackgroundRemovalHelp', {
            detail: { 
                handler: this,
                stepId: this.stepId,
                info: this.definition.backgroundRemovalInfo 
            }
        });
        document.dispatchEvent(event);
    }

    /**
     * Add a removeImage method to ImageStepHandler
     * This method resets just the image while preserving other settings
     */
    removeImage() {
        // Get existing step data
        const stepData = this.getStepData() || {};
        
        // Remove image-related properties but keep position/scale and enabled status
        delete stepData.imageUrl;
        delete stepData.file;
        delete stepData.fileName;
        delete stepData.fileType;
        delete stepData.fileSize;
        delete stepData.uploadTimestamp;
        
        // Keep the step enabled, just without an image
        stepData.enabled = true;
        
        // Get the element
        const elementId = this.definition.elements?.[0];
        const element = this.getElement(elementId);
        
        if (element) {
            // Clear the _lastSrc flag to force reload on next update
            delete element._lastSrc;
            
            // Get placeholder URL - either from template or use default
            const placeholderUrl = '/wp-content/plugins/octo-stc/public/assets/placeholder.svg';
            
            // Force reset to placeholder image
            element.setSrc(placeholderUrl).then(() => {
                // Reset to original dimensions
                if (this.originalDimensions) {
                    element.set({
                        scaleX: this.originalDimensions.scaleX,
                        scaleY: this.originalDimensions.scaleY
                    });
                }
                
                // Force canvas update
                this.canvas.canvas.requestRenderAll();
                
                // Update the step data
                this.setStepData(stepData);
                
                // Use the more reliable panel rebuild method
                this.rebuildPanel();
            }).catch(err => {
                console.error('Error resetting image to placeholder:', err);
                
                // Even if there's an error, update the UI anyway
                this.setStepData(stepData);
                this.rebuildPanel();
            });
        } else {
            // Just update the step data if no element found
            this.setStepData(stepData);
            
            // Use the more reliable panel rebuild method
            this.rebuildPanel();
        }
    }

    /**
     * Ensure the image element exists, creating it if needed
     * @returns {Promise<object>} The fabric element
     */
    async ensureElementExists() {

        let elementId = this.definition.elements?.[0];
        let element = this.getCurrentElement();
        if (element) return element;

        // Element doesn't exist, create it
        element = await this.createImageElement(elementId);
        if (element) {
            this.elements.set(elementId, element);
            
            // Store original dimensions for later use
            this.originalDimensions = {
                width: element.width * element.scaleX,
                height: element.height * element.scaleY,
                scaleX: element.scaleX,
                scaleY: element.scaleY
            };
        }
        
        return element;
    }

    getCurrentElement(){

        const elementId = this.definition.elements?.[0];
        return this.elements.get(elementId);

    }

    /**
     * Create a new image element
     * 
     * @param {string} elementId - The element ID to assign
     * @returns {Promise<object>} The created fabric image object
     */
    async createImageElement(elementId) {
        try {
            // Create a placeholder
            const placeholderUrl = '/wp-content/plugins/octo-stc/public/assets/placeholder.svg';

            return new Promise((resolve) => {
                Image.fromURL(placeholderUrl).then((img) => {

                    let element = this.getCurrentElement();
                    if( element ) return resolve(element);

                    if (!img) {
                        console.error('Failed to create image element');
                        resolve(null);
                        return;
                    }
                    
                    // Set properties (without zIndex)
                    img.set({
                        name: elementId,
                        left: this.canvas.canvas.width / 2,
                        top: this.canvas.canvas.height / 2,
                        originX: 'center',
                        originY: 'center',
                        scaleX: 1,
                        scaleY: 1,
                        selectable: false,
                        hasControls: false,
                        lockMovementX: true,
                        lockMovementY: true
                    });
                    
                    // Add to canvas
                    this.canvas.canvas.add(img);
                    
                    // Register with canvas
                    this.canvas.elementRefs.set(elementId, img);
                    
                    resolve(img);
                });
            });
        } catch (error) {
            console.error('Error creating image element:', error);
            return null;
        }
    }

    /**
     * Completely rebuilds the panel to ensure events are properly bound
     * This is more reliable than just updating content
     */
    rebuildPanel() {
        if (!this.panelElement) return;
        
        // First, unbind all existing events
        this.unbindPanelEvents();
        
        // Generate new content
        this.panelElement.innerHTML = this.renderPanelContent();
        
        // Bind events to the new content
        this.bindPanelEvents();
    }

    
    /**
     * Render panel content with background removal toggle
     */
    renderPanelContent() {
        const stepData = this.getStepData();
        const hasImage = stepData && stepData.imageUrl;
        const showBackgroundRemovalToggle = this.definition.needsBackgroundRemoval !== undefined;
        
        // Build background removal toggle if configured for this step
        const backgroundRemovalToggle = showBackgroundRemovalToggle && hasImage ? `
            <div class="stc-background-removal-toggle">
                <div class="stc-toggle-header">
                    <label class="stc-switch">
                        <input type="checkbox" 
                               class="stc-background-toggle"
                               ${stepData.needsBackgroundRemoval !== false ? 'checked' : ''}>
                        <span class="stc-slider"></span>
                    </label>
                    <span class="stc-toggle-label">
                        Professional Background Removal
                    </span>
                </div>
                <p class="stc-toggle-description">
                    ${this.definition.backgroundRemovalInfo?.description}
                </p>
            </div>
        ` : '';

        return `
            <div class="stc-image-field">
                ${!this.definition.required ? 
                    `
                    <div class="stc-image-toggle">
                        <label class="stc-switch">
                            <input type="checkbox" 
                                   class="stc-image-toggle"
                                   ${stepData?.enabled ? 'checked' : ''}>
                            <span class="stc-slider"></span>
                        </label>
                        <span class="stc-toggle-label">
                            Enable ${this.definition.title}
                        </span>
                    </div>
                    `: ''
                }

                <div class="stc-image-preview ${stepData?.enabled === false ? 'stc-image-disabled' : ''}">
                    ${hasImage ? 
                        `<img src="${stepData.imageUrl}" alt="Uploaded image" 
                              class="${stepData.needsBackgroundRemoval ? 'stc-image-will-be-processed' : ''}">` :
                        '<span>Click or drag to upload</span>'
                    }
                </div>
                
                ${backgroundRemovalToggle}
                
                <div class="stc-image-actions">
                    ${hasImage ? 
                        `<button type="button" class="stc-remove-image-btn">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M3 6h18"></path>
                                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                            </svg>
                            Remove Image
                        </button>` : 
                        ''
                    }
                    <input type="file" 
                        accept="image/*" 
                        class="stc-file-input" 
                        style="display: none">
                </div>
            </div>
        `;
    }
    
    /**
     * Bind panel events including background removal toggle
     */
    bindPanelEvents() {
        // Existing event bindings...
        if (!this.panelElement) return;
        
        // Get UI elements
        const imagePreview = this.panelElement.querySelector('.stc-image-preview');
        const fileInput = this.panelElement.querySelector('.stc-file-input');
        const clearButton = this.panelElement.querySelector('.stc-clear-step');
        const toggleSwitch = this.panelElement.querySelector('.stc-image-toggle input');
        const removeImageBtn = this.panelElement.querySelector('.stc-remove-image-btn');
        const backgroundToggle = this.panelElement.querySelector('.stc-background-toggle');

        // Bind existing events...
        if (toggleSwitch) {
            this.addEventListener(toggleSwitch, 'change', () => {
                const stepData = this.getStepData() || {};
                stepData.enabled = toggleSwitch.checked;
                
                // Update preview visibility
                if (imagePreview) {
                    imagePreview.classList.toggle('stc-image-disabled', !stepData.enabled);
                }
                
                this.setStepData(stepData);
            });
        }
        
        // Bind new background removal toggle
        if (backgroundToggle) {
            this.addEventListener(backgroundToggle, 'change', () => {
                const stepData = this.getStepData() || {};
                stepData.needsBackgroundRemoval = backgroundToggle.checked;
                
                // Update image preview class for visual indication
                const img = this.panelElement.querySelector('.stc-image-preview img');
                if (img) {
                    img.classList.toggle('stc-image-will-be-processed', stepData.needsBackgroundRemoval);
                }
                
                this.setStepData(stepData);
            });
        }
        
        // Continue binding other existing events...
        this.addEventListener(imagePreview, 'click', () => {
            const stepData = this.getStepData() || {};
            if (stepData.enabled !== false) {
                fileInput.click();
            }
        });
        
        this.addEventListener(fileInput, 'change', () => {
            if (fileInput.files && fileInput.files[0]) {
                this.handleImageUpload(fileInput.files[0]);
            }
        });
        
        if (clearButton) {
            this.addEventListener(clearButton, 'click', () => {
                this.clear();
            });
        }
        
        if (removeImageBtn) {
            this.addEventListener(removeImageBtn, 'click', (e) => {
                e.stopPropagation(); // Prevent triggering other events
                this.removeImage();
            });
        }
        
        // Bind drag events
        this.addEventListener(imagePreview, 'dragover', (e) => {
            e.preventDefault();
            const stepData = this.getStepData() || {};
            if (stepData.enabled !== false) {
                imagePreview.classList.add('is-dragover');
            }
        });
        
        this.addEventListener(imagePreview, 'dragleave', () => {
            imagePreview.classList.remove('is-dragover');
        });
        
        this.addEventListener(imagePreview, 'drop', (e) => {
            e.preventDefault();
            imagePreview.classList.remove('is-dragover');
            
            const stepData = this.getStepData() || {};
            if (stepData.enabled !== false) {
                if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                    this.handleImageUpload(e.dataTransfer.files[0]);
                }
            }
        });
    }
    
    /**
     * Attach modification listener to track changes
     * 
     * @param {object} element - Fabric object
     */
    attachModificationListener(element) {
        // Remove existing listener if any
        this.detachModificationListener(element);
        
        // Create new handler
        this.modificationHandler = () => {
            this.handleElementModified(element);
        };
        
        // Attach to element
        element.on('modified', this.modificationHandler);
    }

    /**
     * Detach modification listener
     * 
     * @param {object} element - Fabric object
     */
    detachModificationListener(element) {
        if (this.modificationHandler) {
            element.off('modified', this.modificationHandler);
            this.modificationHandler = null;
        }
    }

    /**
     * Handle element modification (move/scale)
     * 
     * @param {object} element - Modified fabric object
     */
    handleElementModified(element) {
        // Get current step data
        const stepData = this.getStepData() || {};
        
        // Update position and scale
        stepData.left = element.left;
        stepData.top = element.top;
        stepData.scaleX = element.scaleX;
        stepData.scaleY = element.scaleY;
        
        // Update step data without triggering recursive update
        this.setStepData(stepData, false);
    }

    /**
     * Step-specific reset logic
     */
    onReset() {
        // Clear image-specific properties
        const elementId = this.definition.elements?.[0];
        if (!elementId) return;
        
        const element = this.getElement(elementId);
        if (!element) return;
        
        // Clear the _lastSrc flag to force reload on next update
        delete element._lastSrc;
        
        // Get original data
        const elementData = this.canvas.getElementData(elementId);
        if (!elementData || !elementData.data) return;
        
        // Reset to placeholder image if available
        if (elementData.data.src) {
            element.setSrc(elementData.data.src).then(() => {
                // Reset to original dimensions
                if (this.originalDimensions) {
                    element.set({
                        scaleX: this.originalDimensions.scaleX,
                        scaleY: this.originalDimensions.scaleY
                    });
                }
                
                this.canvas.canvas.requestRenderAll();
            });
        }
    }

    /**
     * Get export data for image step
     * Improved to avoid duplication of base64 data
     * 
     * @returns {object} Export data
     */
    getStepExportData() {
        const baseData = super.getStepExportData();
        const stepData = this.getStepData();

        // Enhanced export data with save operation context
        return {
            ...baseData,
            imageRef: stepData.imageId || this.imageId,
            needsBackgroundRemoval: stepData.needsBackgroundRemoval === true,
            hasUploadedImage: !!(stepData.imageUrl && stepData.imageUrl.startsWith('data:')),
            imageMetadata: {
                fileName: stepData.fileName,
                fileType: stepData.fileType,
                fileSize: stepData.fileSize,
                uploadTimestamp: stepData.uploadTimestamp
            },
            positioning: {
                left: stepData.left,
                top: stepData.top,
                scaleX: stepData.scaleX,
                scaleY: stepData.scaleY
            }
        };
    }
    
    /**
     * Prepare image data for export collection
     * This is called by DesignExporter to collect all images
     * 
     * @returns {object|null} Image reference data or null if no image
     */
    getImageReferenceForExport() {
        const stepData = this.getStepData();

        // If no image data, return null
        if (!stepData || !stepData.imageUrl) return null;

        // Determine if this image needs processing (new upload = data: URL)
        const needsProcessing = stepData.imageUrl && stepData.imageUrl.startsWith('data:');

        // If this is an existing upload that hasn't changed, preserve original reference
        if (!needsProcessing && stepData.isExistingUpload) {
            const referenceId = stepData.imageId || this.imageId;
            return {
                referenceId: referenceId,
                type: 'user_upload',
                side: this.side,
                stepId: this.stepId,
                // Preserve the original upload data
                id: stepData.attachmentId,
                url: stepData.existingUrl,
                processed: true,
                metadata: {
                    fileName: stepData.fileName || `image_${this.stepId}.png`,
                    fileType: stepData.fileType || 'image/png',
                    fileSize: stepData.fileSize || 0,
                    needsBackgroundRemoval: stepData.needsBackgroundRemoval === true,
                    positioning: {
                        left: stepData.left,
                        top: stepData.top,
                        scaleX: stepData.scaleX,
                        scaleY: stepData.scaleY
                    }
                }
            };
        }

        // Use the stored imageId or generate one
        const referenceId = stepData.imageId || this.imageId || `image_${this.stepId}_${Date.now()}`;

        // Create the reference object for new uploads
        const reference = {
            referenceId: referenceId,
            stepId: this.stepId,
            side: this.side,
            type: 'user_upload',
            needsProcessing: needsProcessing,
            metadata: {
                fileName: stepData.fileName || `image_${this.stepId}.png`,
                fileType: stepData.fileType || 'image/png',
                fileSize: stepData.fileSize || 0,
                needsBackgroundRemoval: stepData.needsBackgroundRemoval === true,
                positioning: {
                    left: stepData.left,
                    top: stepData.top,
                    scaleX: stepData.scaleX,
                    scaleY: stepData.scaleY
                }
            }
        };

        // Include the actual data URL for images that need processing
        if (needsProcessing) {
            reference.dataUrl = stepData.imageUrl;
        }

        return reference;
    }
    
    /**
     * Clean up resources
     */
    dispose() {
        // Clean up event listeners
        const elementId = this.definition.elements?.[0];
        if (elementId) {
            const element = this.elements.get(elementId);
            if (element) {
                this.detachModificationListener(element);
            }
        }
        
        // Call parent dispose
        super.dispose();
    }

    isComplete() {
        const stepData = this.getStepData();
        return stepData.imageUrl != undefined;
    }

    /**
     * Check if image step has content that needs to be saved
     * 
     * @param {Object} stepData - Current step data
     * @returns {boolean} Whether step has saveable content
     */
    hasSaveableContent(stepData) {
        // Only save if:
        // 1. Step is enabled (or enabled is not explicitly false)
        // 2. We have an image URL
        // 3. The image is a data URL (needs processing) or is a user upload
        return stepData && 
            stepData.enabled !== false && 
            stepData.imageUrl && 
            (stepData.imageUrl.startsWith('data:') || stepData.file);
    }

    /**
     * Create save operations for image content
     * 
     * @param {Object} stepData - Current step data
     * @returns {Promise<Array>} Array of save operation objects
     */
    async createSaveOperations(stepData) {
        const operations = [];
        
        if (!this.hasSaveableContent(stepData)) {
            return operations;
        }
        
        // Use existing imageId or generate a new one
        const imageReferenceId = stepData.imageId || this.imageId || `image_${this.side}_${this.stepId}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        
        // Create the image upload operation
        operations.push({
            type: 'image_upload',
            referenceId: imageReferenceId,
            dataUrl: stepData.imageUrl,
            imageType: 'user_upload',
            metadata: {
                stepId: this.stepId,
                side: this.side,
                fileName: stepData.fileName || `image_${this.stepId}_${this.side}.${this.getFileExtension(stepData.fileType)}`,
                fileType: stepData.fileType || 'image/png',
                fileSize: stepData.fileSize || 0,
                uploadTimestamp: stepData.uploadTimestamp || Date.now(),
                
                // Image processing options
                needsBackgroundRemoval: stepData.needsBackgroundRemoval == true,
                
                // Position and scaling information for reconstruction
                positioning: {
                    left: stepData.left,
                    top: stepData.top,
                    scaleX: stepData.scaleX,
                    scaleY: stepData.scaleY
                },
                
                // Original dimensions for reference
                originalDimensions: this.originalDimensions
            }
        });
        
        // If background removal is enabled, we might want to create additional operations
        if (stepData.needsBackgroundRemoval) {
            // We could add a separate operation for background removal processing
            // but for now, we'll handle it as metadata in the main upload operation
            operations[0].metadata.processingInstructions = {
                removeBackground: true,
                generateThumbnail: true,
                optimizeForPrint: true
            };
        }
        
        // Update step data with reference ID
        if (!stepData.imageId) {
            const updatedStepData = { ...stepData };
            updatedStepData.imageId = imageReferenceId;
            this.setStepData(updatedStepData, false);
        }
        
        return operations;
    }

    /**
     * Get file extension from MIME type
     * 
     * @param {string} mimeType - MIME type (e.g., 'image/jpeg')
     * @returns {string} File extension (e.g., 'jpg')
     */
    getFileExtension(mimeType) {
        const extensions = {
            'image/jpeg': 'jpg',
            'image/jpg': 'jpg',
            'image/png': 'png',
            'image/gif': 'gif',
            'image/webp': 'webp',
            'image/svg+xml': 'svg'
        };
        
        return extensions[mimeType] || 'png';
    }

    /**
     * Get priority for image save operations
     * User uploaded images are high priority, especially profile images
     * 
     * @returns {number} Priority level (0-10)
     */
    getSaveOperationsPriority() {
        // Profile/main images get highest priority
        if (this.stepId === 'image' || this.stepId === 'client_image' || this.stepId === 'profile_image') {
            return 10; // Highest priority
        }
        
        // Logo images get high priority
        if (this.stepId.includes('logo')) {
            return 8;
        }
        
        // Other user uploads get medium-high priority
        return 6;
    }

    /**
     * Validate that image save operations completed successfully
     * 
     * @param {Array} completedOperations - Operations that were processed
     * @returns {boolean} Whether this step's requirements were met
     */
    validateSaveOperations(completedOperations) {
        const stepData = this.getStepData();
        
        // If no image content, validation passes
        if (!this.hasSaveableContent(stepData)) {
            return true;
        }
        
        // Look for our image operation in the completed operations
        const imageOperation = completedOperations.find(op => 
            op.sourceStepId === this.stepId && 
            op.sourceSide === this.side &&
            op.type === 'image_upload' &&
            (op.referenceId === stepData.imageId || op.referenceId === this.imageId)
        );
        
        if (!imageOperation) {
            console.error(`Image save operation not found for ${this.stepId}`);
            return false;
        }
        
        // Check if the operation has a result with a URL
        if (!imageOperation.result || !imageOperation.result.url) {
            console.error(`Image save operation failed for ${this.stepId}:`, imageOperation.error);
            return false;
        }
        
        // Additional validation for background removal if requested
        if (stepData.needsBackgroundRemoval && imageOperation.result) {
            // We could add specific validation for background removal results here
            // For now, we'll just log the result
            console.log(`Image with background removal processed for ${this.stepId}:`, imageOperation.result);
        }
        
        console.log(`Image save validation passed for ${this.stepId}:`, imageOperation.result.url);
        return true;
    }

}